import { useState } from "react";
import {
  FileText,
  Copy,
  Trash2,
  Plus,
  MoreHorizontal,
  Pencil,
  LayoutTemplate,
  LayoutGrid,
  List,
  FolderOpen,
  Handshake,
  Users,
  DollarSign,
  Scale,
  Home,
  Heart,
  Folder,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import {
  ActionSheet,
  ActionSheetContent,
  ActionSheetItem,
  ActionSheetSeparator,
} from "@/components/ui/bottom-sheet";
import { Button, FloatingActionButton } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  SegmentedControl,
  SegmentedControlItem,
} from "@/components/ui/mobile-nav";
import { PdfThumbnail } from "@/components/ui/pdf-thumbnail";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useIsMobile } from "@/hooks/useMobileUtils";
import {
  useTemplates,
  usePublicTemplates,
  useTemplateCategories,
  useDeleteTemplate,
  useDuplicateTemplate,
} from "@/hooks/useQueries";
import { OnboardingBanner } from "@/pages/onboarding/components";

const CATEGORY_ICONS = {
  Contracts: FolderOpen,
  Agreements: Handshake,
  "HR & Employment": Users,
  Finance: DollarSign,
  Legal: Scale,
  "Real Estate": Home,
  Healthcare: Heart,
  Other: Folder,
};

const CategoryIcon = ({ category, className = "h-4 w-4" }) => {
  if (!category) return null;
  const IconComponent = CATEGORY_ICONS[category.name] || Folder;
  return <IconComponent className={className} />;
};

const TemplateCard = ({
  template,
  categories,
  isOwner,
  onUse,
  onEdit,
  onDuplicate,
  onDelete,
}) => {
  const isMobile = useIsMobile();
  const [actionSheetOpen, setActionSheetOpen] = useState(false);
  const category = categories.find((c) => c.id === template.category);

  // Only attempt to load thumbnail if template has a document
  const thumbnailUrl = template.documentStoragePath
    ? `${import.meta.env.VITE_API_URL || "/api/v1"}/templates/${template.id}/document`
    : null;

  const handleMoreClick = (e) => {
    e.stopPropagation();
    if (isMobile) setActionSheetOpen(true);
  };

  const pageCount = template.documentPageCount || 1;
  const pageLabel = `${pageCount} page${pageCount !== 1 ? "s" : ""}`;

  return (
    <>
      <Card
        className="group overflow-hidden card-pressable cursor-pointer"
        onClick={() => onUse(template)}
      >
        <div className="h-40 bg-muted flex items-center justify-center relative">
          {thumbnailUrl ? (
            <PdfThumbnail
              url={thumbnailUrl}
              cacheKey={`template-${template.id}`}
              className="w-full h-full"
              width={250}
              fallback={
                <div className="text-center">
                  <FileText
                    className="h-8 w-8 text-muted-foreground mx-auto mb-1"
                    aria-hidden="true"
                  />
                  <p className="text-xs text-muted-foreground">{pageLabel}</p>
                </div>
              }
              overlay={
                <div className="absolute bottom-2 left-2">
                  <Badge
                    variant="secondary"
                    className="text-xs bg-background/80"
                  >
                    {pageLabel}
                  </Badge>
                </div>
              }
            />
          ) : (
            <div className="text-center">
              <FileText
                className="h-8 w-8 text-muted-foreground mx-auto mb-1"
                aria-hidden="true"
              />
              <p className="text-xs text-muted-foreground">{pageLabel}</p>
            </div>
          )}
          <div className="absolute top-2 right-2 z-10">
            {isMobile ? (
              <Button
                variant="secondary"
                size="icon"
                className="h-10 w-10 touch-target"
                onClick={handleMoreClick}
              >
                <MoreHorizontal className="h-5 w-5" />
              </Button>
            ) : (
              <DropdownMenu>
                <DropdownMenuTrigger
                  asChild
                  onClick={(e) => e.stopPropagation()}
                >
                  <Button variant="secondary" size="icon" className="h-8 w-8">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      onUse(template);
                    }}
                  >
                    <FileText className="mr-2 h-4 w-4" />
                    Use template
                  </DropdownMenuItem>
                  {isOwner && (
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        onEdit(template);
                      }}
                    >
                      <Pencil className="mr-2 h-4 w-4" />
                      Edit
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      onDuplicate(template.id);
                    }}
                  >
                    <Copy className="mr-2 h-4 w-4" />
                    {isOwner ? "Duplicate" : "Copy to my templates"}
                  </DropdownMenuItem>
                  {isOwner && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete(template);
                        }}
                        className="text-destructive focus:text-destructive"
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        Delete
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
        <CardContent className="p-4 dark:bg-background">
          <h3 className="font-medium truncate mb-1">{template.name}</h3>
          {template.description && (
            <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
              {template.description}
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            {category && (
              <Badge
                variant="secondary"
                className="text-xs flex items-center gap-1"
              >
                <CategoryIcon category={category} className="h-3 w-3" />{" "}
                {category.name}
              </Badge>
            )}
            {template.fieldCount > 0 && (
              <Badge variant="outline" className="text-xs">
                {template.fieldCount} fields
              </Badge>
            )}
            {(template.usageCount > 0 || template.usage_count > 0) && (
              <Badge variant="outline" className="text-xs">
                {template.usageCount || template.usage_count || 0} uses
              </Badge>
            )}
            {template.isPublic && (
              <Badge variant="outline" className="text-xs">
                Public
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>
      {isMobile && (
        <ActionSheet open={actionSheetOpen} onOpenChange={setActionSheetOpen}>
          <ActionSheetContent title={template.name}>
            <ActionSheetItem
              onClick={() => {
                setActionSheetOpen(false);
                onUse(template);
              }}
            >
              <FileText className="h-5 w-5" />
              Use template
            </ActionSheetItem>
            {isOwner && (
              <ActionSheetItem
                onClick={() => {
                  setActionSheetOpen(false);
                  onEdit(template);
                }}
              >
                <Pencil className="h-5 w-5" />
                Edit
              </ActionSheetItem>
            )}
            <ActionSheetItem
              onClick={() => {
                setActionSheetOpen(false);
                onDuplicate(template.id);
              }}
            >
              <Copy className="h-5 w-5" />
              {isOwner ? "Duplicate" : "Copy to my templates"}
            </ActionSheetItem>
            {isOwner && (
              <>
                <ActionSheetSeparator />
                <ActionSheetItem
                  destructive
                  onClick={() => {
                    setActionSheetOpen(false);
                    onDelete(template);
                  }}
                >
                  <Trash2 className="h-5 w-5" />
                  Delete
                </ActionSheetItem>
              </>
            )}
          </ActionSheetContent>
        </ActionSheet>
      )}
    </>
  );
};

const TemplateListRow = ({
  template,
  categories,
  isOwner,
  onUse,
  onEdit,
  onDuplicate,
  onDelete,
}) => {
  const category = categories.find((c) => c.id === template.category);
  return (
    <TableRow className="cursor-pointer" onClick={() => onUse(template)}>
      <TableCell>
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
            <FileText className="h-5 w-5 text-primary" />
          </div>
          <div>
            <p className="font-medium">{template.name}</p>
            {template.description && (
              <p className="text-sm text-muted-foreground line-clamp-1">
                {template.description}
              </p>
            )}
          </div>
        </div>
      </TableCell>
      <TableCell>
        {category && (
          <Badge
            variant="secondary"
            className="text-xs flex items-center gap-1 w-fit"
          >
            <CategoryIcon category={category} className="h-3 w-3" />{" "}
            {category.name}
          </Badge>
        )}
      </TableCell>
      <TableCell>
        <Badge variant="outline" className="text-xs">
          {template.fieldCount || 0} fields
        </Badge>
      </TableCell>
      <TableCell className="text-muted-foreground">
        {template.usageCount || template.usage_count || 0}
      </TableCell>
      <TableCell>
        <Badge variant={template.isPublic ? "default" : "secondary"}>
          {template.isPublic ? "Public" : "Private"}
        </Badge>
      </TableCell>
      <TableCell onClick={(e) => e.stopPropagation()}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onUse(template)}>
              <FileText className="mr-2 h-4 w-4" />
              Use template
            </DropdownMenuItem>
            {isOwner && (
              <DropdownMenuItem onClick={() => onEdit(template)}>
                <Pencil className="mr-2 h-4 w-4" />
                Edit
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={() => onDuplicate(template.id)}>
              <Copy className="mr-2 h-4 w-4" />
              {isOwner ? "Duplicate" : "Copy to my templates"}
            </DropdownMenuItem>
            {isOwner && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => onDelete(template)}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>
    </TableRow>
  );
};

const TemplatesSkeleton = () => {
  const isMobile = useIsMobile();
  return (
    <div className="space-y-6">
      <div className="flex justify-between">
        <div className="space-y-2">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-64" />
        </div>
        {!isMobile && <Skeleton className="h-10 w-40" />}
      </div>
      <div className="flex gap-4">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-10 w-48" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="rounded-lg border bg-card overflow-hidden">
            <Skeleton className="h-40 rounded-none" />
            <div className="p-4 space-y-2">
              <Skeleton className="h-5 w-3/4" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-1/2" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const EmptyState = ({ type, onCreateNew }) => {
  const { t } = useTranslation();
  const isMyTemplates = type === "my";
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted mb-4">
        <LayoutTemplate className="h-8 w-8 text-muted-foreground" />
      </div>
      <h3 className="text-lg font-semibold mb-1">
        {isMyTemplates
          ? t("templates.noTemplates")
          : t("templates.noPublicTemplates")}
      </h3>
      <p className="text-sm text-muted-foreground mb-4 max-w-sm">
        {isMyTemplates
          ? t("templates.noTemplatesDesc")
          : t("templates.noPublicTemplatesDesc")}
      </p>
      {isMyTemplates && (
        <Button onClick={onCreateNew} size="lg" className="touch-target">
          <Plus className="mr-2 h-4 w-4" />
          {t("templates.createTemplate")}
        </Button>
      )}
    </div>
  );
};

export const Templates = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [activeTab, setActiveTab] = useState("my");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [viewMode, setViewMode] = useState("grid");
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    template: null,
  });

  const categoryFilter =
    selectedCategory === "all" ? undefined : selectedCategory;
  const { data: categoriesData, isLoading: categoriesLoading } =
    useTemplateCategories();
  const categories = categoriesData || [];
  const { data: myTemplatesData, isLoading: myTemplatesLoading } = useTemplates(
    { category: categoryFilter },
  );
  const templates = myTemplatesData?.data || myTemplatesData || [];
  const { data: publicTemplatesData, isLoading: publicTemplatesLoading } =
    usePublicTemplates({ category: categoryFilter });
  const publicTemplates = publicTemplatesData || [];
  const deleteTemplate = useDeleteTemplate();
  const duplicateTemplate = useDuplicateTemplate();
  const isLoading =
    categoriesLoading ||
    (activeTab === "my" ? myTemplatesLoading : publicTemplatesLoading);
  const displayTemplates = activeTab === "my" ? templates : publicTemplates;

  const handleDeleteTemplate = (template) =>
    setDeleteDialog({ open: true, template });
  const confirmDelete = () => {
    if (!deleteDialog.template) return;
    deleteTemplate.mutate(deleteDialog.template.id, {
      onSuccess: () => setDeleteDialog({ open: false, template: null }),
    });
  };
  const handleDuplicateTemplate = (id) => duplicateTemplate.mutate(id);
  const handleEditTemplate = (template) =>
    navigate(`/templates/${template.id}/edit`);
  const handleUseTemplate = (template) =>
    navigate("/envelopes/new", { state: { templateId: template.id } });

  const renderTemplatesList = () =>
    isMobile || viewMode === "grid" ? (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {displayTemplates.map((template) => (
          <TemplateCard
            key={template.id}
            template={template}
            categories={categories}
            isOwner={activeTab === "my"}
            onUse={handleUseTemplate}
            onEdit={handleEditTemplate}
            onDuplicate={handleDuplicateTemplate}
            onDelete={handleDeleteTemplate}
          />
        ))}
      </div>
    ) : (
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("common.template")}</TableHead>
              <TableHead>{t("common.category")}</TableHead>
              <TableHead>{t("fields.title")}</TableHead>
              <TableHead>{t("common.uses")}</TableHead>
              <TableHead>{t("common.visibility")}</TableHead>
              <TableHead className="w-12.5" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {displayTemplates.map((template) => (
              <TemplateListRow
                key={template.id}
                template={template}
                categories={categories}
                isOwner={activeTab === "my"}
                onUse={handleUseTemplate}
                onEdit={handleEditTemplate}
                onDuplicate={handleDuplicateTemplate}
                onDelete={handleDeleteTemplate}
              />
            ))}
          </TableBody>
        </Table>
      </Card>
    );

  if (isLoading && templates.length === 0 && publicTemplates.length === 0) {
    return <TemplatesSkeleton />;
  }

  return (
    <div className="space-y-6 pb-20 sm:pb-0">
      <OnboardingBanner />

      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold font-heading tracking-tight">
            {t("templates.title")}
          </h1>
          <p className="text-muted-foreground">{t("templates.subtitle")}</p>
        </div>
        {!isMobile && (
          <Button onClick={() => navigate("/templates/new")}>
            <Plus className="mr-2 h-4 w-4" />
            {t("templates.newTemplate")}
          </Button>
        )}
      </div>

      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
          {isMobile ? (
            <SegmentedControl value={activeTab} onValueChange={setActiveTab}>
              <SegmentedControlItem value="my">
                {t("templates.myTemplates")}
              </SegmentedControlItem>
              <SegmentedControlItem value="public">
                {t("templates.publicTemplates")}
              </SegmentedControlItem>
            </SegmentedControl>
          ) : (
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList>
                <TabsTrigger value="my">
                  {t("templates.myTemplates")}
                </TabsTrigger>
                <TabsTrigger value="public">
                  {t("templates.publicTemplates")}
                </TabsTrigger>
              </TabsList>
            </Tabs>
          )}
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className={isMobile ? "w-full h-11" : "w-50"}>
              <div className="flex items-center gap-2">
                {selectedCategory !== "all" && (
                  <CategoryIcon
                    category={categories.find((c) => c.id === selectedCategory)}
                    className="h-4 w-4"
                  />
                )}
                <span>
                  {selectedCategory === "all"
                    ? t("templates.allCategories")
                    : categories.find((c) => c.id === selectedCategory)?.name ||
                      t("templates.allCategories")}
                </span>
              </div>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">
                {t("templates.allCategories")}
              </SelectItem>
              {categories.map((cat) => (
                <SelectItem key={cat.id} value={cat.id}>
                  <span className="flex items-center gap-2">
                    <CategoryIcon category={cat} className="h-4 w-4" />{" "}
                    {cat.name}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {!isMobile && (
          <div className="flex items-center border rounded-lg p-1">
            <Button
              variant={viewMode === "grid" ? "secondary" : "ghost"}
              size="sm"
              className="h-8 px-2"
              onClick={() => setViewMode("grid")}
            >
              <LayoutGrid className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === "list" ? "secondary" : "ghost"}
              size="sm"
              className="h-8 px-2"
              onClick={() => setViewMode("list")}
            >
              <List className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>

      {displayTemplates.length === 0 ? (
        <Card>
          <CardContent className="p-0">
            <EmptyState
              type={activeTab}
              onCreateNew={() => navigate("/templates/new")}
            />
          </CardContent>
        </Card>
      ) : (
        renderTemplatesList()
      )}

      {isMobile && (
        <FloatingActionButton onClick={() => navigate("/templates/new")}>
          <Plus className="h-6 w-6" />
        </FloatingActionButton>
      )}

      <Dialog
        open={deleteDialog.open}
        onOpenChange={(open) =>
          !open && setDeleteDialog({ open: false, template: null })
        }
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("templates.deleteTemplate")}</DialogTitle>
            <DialogDescription>
              {t("templates.deleteConfirmation")}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm font-medium">{deleteDialog.template?.name}</p>
            {deleteDialog.template?.description && (
              <p className="text-sm text-muted-foreground mt-1">
                {deleteDialog.template.description}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialog({ open: false, template: null })}
              disabled={deleteTemplate.isPending}
              size={isMobile ? "mobile" : "default"}
            >
              {t("common.cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              disabled={deleteTemplate.isPending}
              size={isMobile ? "mobile" : "default"}
            >
              {deleteTemplate.isPending
                ? t("common.loading")
                : t("templates.delete")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Templates;
